import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsService } from '../api/statsService';
import { streakService } from '../api/streakService';
import { userSettingService } from '../api/userSettings';

/**
 * 0 = Beginner, 1 = Intermediate, 2 = Fluent/Advanced (full form, matches
 * pre-tier behavior exactly). Drives progressive disclosure in addWord.tsx /
 * editWord.tsx. Mirrors weavewords-mobile's src/hooks/useProficiencyTier.ts —
 * keep both in sync.
 */
export type ProficiencyTier = 0 | 1 | 2;

const DIFFICULTY_TIER: Record<string, ProficiencyTier> = {
    Beginner: 0,
    Intermediate: 1,
    Advanced: 2,
};

const TIER_DIFFICULTY: Record<ProficiencyTier, string> = {
    0: 'Beginner',
    1: 'Intermediate',
    2: 'Advanced',
};

/**
 * Pure difficulty_level -> tier lookup, for callers that only need the
 * current tier (e.g. gating a nav item) and must NOT pull in the full
 * useProficiencyTier() hook below — that hook independently re-fetches
 * overview/streak stats to compute promotion offers, which would duplicate
 * fetches already made elsewhere (e.g. Navigation's own StatsRefreshContext
 * + streakService.getMyStreak() calls).
 */
export function getTierFromDifficulty(difficultyLevel?: string): ProficiencyTier {
    return DIFFICULTY_TIER[difficultyLevel ?? ''] ?? 0;
}

const TIER_1_WORDS = 20;
const TIER_1_XP = 200;
const TIER_2_WORDS = 100;
const TIER_2_STREAK = 14;

function usageTierFrom(totalWords: number, totalXp: number, currentStreak: number): ProficiencyTier {
    if (totalWords >= TIER_2_WORDS || currentStreak >= TIER_2_STREAK) return 2;
    if (totalWords >= TIER_1_WORDS || totalXp >= TIER_1_XP) return 1;
    return 0;
}

/**
 * The word form's complexity tier IS the profile's saved Difficulty setting
 * (Preferences tab) — a single source of truth, editable there like any
 * other setting. Crossing a usage milestone (words added / XP / streak)
 * offers to promote that setting, the same as if the user had picked a
 * higher difficulty themselves — but it never applies the change silently:
 * `pendingPromotion` surfaces it and the caller must call `confirmPromotion()`
 * before `tier` (and the saved setting) actually changes. This also means it
 * only ever offers a promotion, never a demotion, and never overrides a
 * difficulty the user just picked without asking first.
 *
 * The highest tier ever offered is persisted server-side, on the same
 * learning_settings the Difficulty Level itself lives on — once the user has
 * responded to an offer for tier N (confirm OR dismiss), it's never offered
 * again on any device/browser, even if they later manually lower the saved
 * difficulty back below N in Preferences. Only crossing a usage milestone
 * that earns a tier HIGHER than anything already offered triggers a new
 * prompt. (Previously this lived only in localStorage, so it reset on a
 * different browser/device or cleared site data — now it's part of the
 * user's account, like every other setting here.)
 */
export function useProficiencyTier(): {
    tier: ProficiencyTier;
    pendingPromotion: ProficiencyTier | null;
    promotionError: boolean;
    confirmPromotion: () => void;
    dismissPromotion: () => void;
} {
    const auth = useAuth();
    const difficultyLevel = auth?.userSettings?.learning_settings?.difficulty_level ?? 'Beginner';
    const tier = DIFFICULTY_TIER[difficultyLevel] ?? 0;
    const [pendingPromotion, setPendingPromotion] = useState<ProficiencyTier | null>(null);
    const [promotionError, setPromotionError] = useState(false);
    const lastOfferedTier = auth?.userSettings?.learning_settings?.last_offered_proficiency_tier ?? null;

    // Tracks the latest userSettings seen, so a delayed rollback (see confirmPromotion)
    // can merge back onto whatever is current instead of a snapshot from click time.
    const userSettingsRef = useRef(auth?.userSettings);
    userSettingsRef.current = auth?.userSettings;

    // Persists `offeredTier` onto learning_settings (merged with whatever else is
    // there — the backend replaces the whole learning_settings blob on save, so a
    // partial object here would silently wipe difficulty_level and the rest).
    // Re-fetches the account's current settings right before merging/writing —
    // this hook's in-memory auth.userSettings can be stale relative to another
    // open tab/device that changed an unrelated learning setting since this tab
    // last loaded them, and PUTting a stale blob would silently revert that
    // change. Falls back to the in-memory copy if the refresh itself fails.
    // The network write is otherwise fire-and-forget since a failure here only
    // risks the prompt resurfacing once more, not data loss.
    async function persistOffered(offeredTier: ProficiencyTier, extra: Partial<{ difficulty_level: string }> = {}) {
        if (!auth?.userSettings?.learning_settings) return;
        const fallbackSettings = auth.userSettings;
        const fallbackLearningSettings = auth.userSettings.learning_settings;

        // Apply the optimistic update to local state synchronously, before the
        // network round trip below — a second promotion earned in the same
        // session (or the effect above re-running against a concurrent
        // updateUserSettings call elsewhere) must see the new tier/difficulty
        // immediately, not after a GET+PUT resolves.
        auth.updateUserSettings({
            ...fallbackSettings,
            learning_settings: {
                ...fallbackLearningSettings,
                ...extra,
                last_offered_proficiency_tier: offeredTier,
            },
        });

        let baseSettings: typeof fallbackSettings = fallbackSettings;
        try {
            const fresh = await userSettingService.getUserSettings();
            if (fresh?.learning_settings) baseSettings = fresh;
        } catch {
            // Refresh failed — proceed with the in-memory copy.
        }
        const nextLearningSettings = {
            ...(baseSettings.learning_settings ?? fallbackLearningSettings),
            ...extra,
            last_offered_proficiency_tier: offeredTier,
        };
        // Re-apply once more in case the refresh surfaced settings changed
        // elsewhere (another tab/device) since the optimistic update above —
        // this is what actually gets persisted server-side next.
        auth.updateUserSettings({ ...baseSettings, learning_settings: nextLearningSettings });
        return userSettingService.updateSettings({ learning_settings: nextLearningSettings });
    }

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!auth?.userSettings?.learning_settings) return;
            try {
                const [overview, streak] = await Promise.all([
                    statsService.getOverview(auth.userSettings.learning_language ?? ''),
                    streakService.getMyStreak(),
                ]);
                if (cancelled) return;
                const earnedTier = usageTierFrom(overview.total_words, streak.total_xp, streak.current_streak);
                if (earnedTier > tier && earnedTier > (lastOfferedTier ?? -1)) {
                    setPendingPromotion(earnedTier);
                }
            } catch {
                // Usage stats unavailable — leave the saved setting as-is.
            }
        })();
        return () => { cancelled = true; };
    }, [auth?.userSettings?.learning_settings, auth?.userSettings?.learning_language, tier, lastOfferedTier]);

    function confirmPromotion() {
        if (pendingPromotion === null || !auth?.userSettings?.learning_settings) return;
        const confirmedTier = pendingPromotion;
        const previousDifficultyLevel = auth.userSettings.learning_settings.difficulty_level;
        const previousLastOfferedTier = auth.userSettings.learning_settings.last_offered_proficiency_tier;
        setPendingPromotion(null);
        setPromotionError(false);
        persistOffered(confirmedTier, { difficulty_level: TIER_DIFFICULTY[confirmedTier] })
            ?.catch(() => {
                // Roll back only the two fields this save touched, merged onto whatever
                // learning_settings is current (not the click-time snapshot) — an unrelated
                // setting changed elsewhere while this save was in flight must not be
                // discarded by the rollback.
                const currentUserSettings = userSettingsRef.current ?? auth.userSettings;
                if (currentUserSettings?.learning_settings) {
                    auth.updateUserSettings({
                        ...currentUserSettings,
                        learning_settings: {
                            ...currentUserSettings.learning_settings,
                            difficulty_level: previousDifficultyLevel,
                            last_offered_proficiency_tier: previousLastOfferedTier,
                        },
                    });
                }
                setPromotionError(true);
            });
    }

    function dismissPromotion() {
        if (pendingPromotion !== null) persistOffered(pendingPromotion)?.catch(() => {});
        setPendingPromotion(null);
        setPromotionError(false);
    }

    return { tier, pendingPromotion, promotionError, confirmPromotion, dismissPromotion };
}
